/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Box, Typography, Grid } from "@mui/material";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { getAPICall } from "lib/callApi";
import Trend from "pages/Performance/components/Trend";
import { THEMES } from "lib/constants";
import {
  fetchDateRangeForWidget,
  formatDatesForWidget,
  handlePeriodsForWidget,
} from "utils/HelperFunctions";
import "../../styles/widget.scss";

export default function ListingSnapshot({ selectedUser, selectedTab, widget }) {
  const { dataSource, widgetTitle, displayName } = widget;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [periodDates, setPeriodDates] = useState({});
  const [snapshotData, setSnapshotData] = useState([]);
  const [displayData, setDisplayData] = useState({});

  const timePeriod = "monthly";
  const [loading, setLoading] = useState(!!widget?.connectedStatus);

  const calculateDisplayData = () => {
    if (snapshotData?.length) {
      let display = {
        currentListings: 0,
        avgDaysOnMarket: 0,
        avgDaysOnMarketTrend: 0,
        avgDaysStartToClose: 0,
        cancellationRate: 0,
      };
      if (selectedTab) {
        if (selectedUser) {
          const user = snapshotData.find((x) => x.userId === selectedUser);
          if (user) {
            display = {
              currentListings: user.currentListings,
              avgDaysOnMarket: user.avgDaysOnMarket,
              avgDaysOnMarketTrend: user.avgDaysOnMarketTrend,
              avgDaysStartToClose: user.avgDaysStartToClose,
              cancellationRate: user.cancellationRate,
            };
          }
        } else {
          display = snapshotData.reduce(
            (acc, curr) => ({
              currentListings: acc.currentListings + curr.currentListings,
              avgDaysOnMarket: acc.avgDaysOnMarket + curr.avgDaysOnMarket,
              avgDaysOnMarketTrend: acc.avgDaysOnMarketTrend + curr.avgDaysOnMarketTrend,
              avgDaysStartToClose: acc.avgDaysStartToClose + curr.avgDaysStartToClose,
              cancellationRate: acc.cancellationRate + curr.cancellationRate,
            }),
            display
          );
          display.avgDaysStartToClose = (display.avgDaysStartToClose / snapshotData.length).toFixed(
            1
          );
          display.avgDaysOnMarket = (display.avgDaysOnMarket / snapshotData.length).toFixed(1);
          display.avgDaysOnMarketTrend = (
            display.avgDaysOnMarketTrend / snapshotData.length
          ).toFixed(1);
        }
      } else {
        const user = snapshotData.find((x) => x.userCompanyLinkId === loginUser.userCompanyLinkId);
        if (user) {
          display = {
            currentListings: user.currentListings,
            avgDaysOnMarket: user.avgDaysOnMarket,
            avgDaysOnMarketTrend: user.avgDaysOnMarketTrend,
            avgDaysStartToClose: user.avgDaysStartToClose,
            cancellationRate: user.cancellationRate,
          };
        }
      }
      setDisplayData(display);
    }
  };

  const handlePeriods = (type) => {
    if (!periodDates.start && !periodDates.end) return;
    const dates = handlePeriodsForWidget(type, timePeriod, periodDates);
    if (
      dates.start &&
      dates.end &&
      dates.start !== periodDates.start &&
      dates.end !== periodDates.end
    ) {
      setPeriodDates(dates);
    }
  };

  const fetchMonthlySnapshotData = async () => {
    if (widget.connectedStatus && dataSource && periodDates.start && periodDates.end) {
      setLoading(true);
      const result = await getAPICall(dataSource, true, {
        start: periodDates.start,
        end: periodDates.end,
      });
      if (result?.data?.success) {
        setSnapshotData(result.data.data);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    calculateDisplayData();
  }, [snapshotData, selectedTab, selectedUser]);

  useEffect(() => {
    fetchMonthlySnapshotData();
  }, [periodDates]);

  useEffect(() => {
    const dates = fetchDateRangeForWidget(timePeriod);
    setPeriodDates(dates);
  }, []);

  return (
    <Box p={2}>
      <Box
        sx={{
          pl: 1,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          "& .big-text": { fontSize: "1rem", fontWeight: "bold" },
        }}
      >
        {selectedTab ? (
          <Typography
            variant="h6"
            color="info"
            mb={0}
            display="block"
            className="dragHandle"
            lineHeight="1rem"
          >
            Team {widgetTitle || displayName}
          </Typography>
        ) : (
          <Typography
            variant="h6"
            color="info"
            mb={0}
            display="block"
            className="dragHandle"
            lineHeight="1rem"
          >
            My {widgetTitle || displayName}
          </Typography>
        )}
        {!!(widget.connectedStatus && periodDates.start && !loading) && (
          <Box
            sx={{
              fontSize: "20px",
              display: "flex",
              alignItems: "center",
              mx: "-10px",
            }}
          >
            <ArrowLeft
              style={{ cursor: "pointer" }}
              onClick={() => handlePeriods("prev")}
              sx={{
                mr: "-3px",
              }}
            />
            <Typography variant="caption" color="info" mb={0} display="block">
              {formatDatesForWidget(timePeriod, periodDates)}
            </Typography>
            <ArrowRight
              style={{ cursor: "pointer" }}
              onClick={() => handlePeriods("next")}
              sx={{
                ml: "-3px",
              }}
            />
          </Box>
        )}
      </Box>

      {!!(widget.connectedStatus && !loading) && (
        <Grid
          container
          sx={{
            justifyContent: "space-between",
          }}
          wrap="nowrap"
        >
          <Grid item pl={1}>
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                height: "100%",
                overflowWrap: "anywhere",
              }}
            >
              <Grid item>
                <Grid
                  container
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Grid item>
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        lineHeight: 1.25,
                        // color: THEMES.PRIMARY.RED,
                      }}
                    >
                      {displayData.currentListings}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="caption" color="info" mb={0} display="block">
                      Current Listings
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid
                  container
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Grid
                    item
                    pt={1}
                    sx={{
                      display: "inline-flex",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        lineHeight: 1.25,
                        color:
                          displayData.avgDaysOnMarketTrend > 0
                            ? THEMES.PRIMARY.RED_TREND
                            : THEMES.PRIMARY.GREEN_TREND,
                      }}
                    >
                      {displayData.avgDaysOnMarket}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: "60%",
                        pt: "0.25rem",
                      }}
                    >
                      <Trend
                        prev={0}
                        curr={displayData.avgDaysOnMarketTrend}
                        type="percent"
                        explicit
                        inverse
                      />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="caption" color="info" mb={0} display="block">
                      Avg Days on Market
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item pl={1}>
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                height: "100%",
                overflowWrap: "anywhere",
              }}
            >
              <Grid item>
                <Grid
                  container
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Grid item>
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {displayData.avgDaysStartToClose}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="caption" color="info" mb={0} display="block">
                      Avg Days Start to Close
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid
                  container
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Grid item pt={1}>
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {displayData.cancellationRate}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="caption" color="info" mb={0} display="block">
                      Cancellation Rate
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!!(!widget.connectedStatus && !loading) && (
        <Grid
          container
          sx={{
            justifyContent: "space-between",
            height: "100%",
          }}
          wrap="nowrap"
        >
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              height: "100%",
              alignItems: "end",
            }}
          >
            <LinkOffIcon fontSize="large" />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
